import { Icon } from '../../../widgets/icon';
import { InputDialog, QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import React, { FC, useCallback, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { T, stringFormat } from 'src/shared/helpers';
import PencilIcon from 'src/shared/material_icons/image/edit.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import styled from 'styled-components';
import {
    AIAssetLibrarySortBy,
    SortDirection,
    assetLibraryQueriesAIAssetLibraryCategoriesQuery,
    assetLibraryQueriesAIAssetLibraryCategoriesQuery$data
} from './__generated__/assetLibraryQueriesAIAssetLibraryCategoriesQuery.graphql';
import { assetLibraryQueriesDeleteAIAssetLibraryCategoryMutation } from './__generated__/assetLibraryQueriesDeleteAIAssetLibraryCategoryMutation.graphql';
import { assetLibraryQueriesUpdateAIAssetLibraryCategoryMutation } from './__generated__/assetLibraryQueriesUpdateAIAssetLibraryCategoryMutation.graphql';
import { aiAssetLibraryCategoriesQuery, deleteAIAssetLibraryCategoryMutation, updateAIAssetLibraryCategoryMutation } from './assetLibraryQueries';

type CategoryNode = NonNullable<NonNullable<assetLibraryQueriesAIAssetLibraryCategoriesQuery$data['aiAssetLibraryCategories']>['nodes']>[number];

const TreeContent = styled.div`
    padding: 10px 10px;
`;

const CategoryItem = styled.div<{ $selected: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    margin-bottom: 5px;
    cursor: pointer;
    background: ${(props) => (props.$selected ? '#e5e7eb' : 'transparent')};
    border-radius: 8px;
    transition: all 0.2s;

    &:hover {
        background: #e5e7eb;
    }
`;

const CategoryName = styled.span<{ $selected: boolean }>`
    font-size: 14px;
    font-weight: ${(props) => (props.$selected ? 600 : 400)};
    color: ${(props) => props.theme.app.textColor};
    flex: 1;
`;

const CategoryActions = styled.div`
    display: flex;
    gap: 8px;
    opacity: 0;
    transition: opacity 0.2s;

    ${CategoryItem}:hover & {
        opacity: 1;
    }
`;

const ActionIcon = styled(Icon)`
    cursor: pointer;

    &:hover svg {
        fill: ${(props) => props.theme.app.primary};
    }
`;

interface AssetCategoriesTreeProps {
    selectedCategoryId: string | null;
    onCategorySelect: (categoryId: string | null) => void;
    searchName: string;
    sortBy: AIAssetLibrarySortBy;
    sortDirection: SortDirection;
    externalRefetchKey?: number;
}

export const AssetCategoriesTree: FC<AssetCategoriesTreeProps> = ({
    selectedCategoryId,
    onCategorySelect,
    searchName,
    sortBy,
    sortDirection,
    externalRefetchKey = 0
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [refetchKey, setRefetchKey] = useState(0);

    const categoriesData = useLazyLoadQuery<assetLibraryQueriesAIAssetLibraryCategoriesQuery>(
        aiAssetLibraryCategoriesQuery,
        {
            searchName: searchName || undefined,
            sortBy,
            sortDirection
        },
        { fetchKey: refetchKey + externalRefetchKey, fetchPolicy: 'store-and-network' }
    );

    const categories = categoriesData.aiAssetLibraryCategories?.nodes || [];

    const handleRefetch = useCallback(() => {
        setRefetchKey((key) => key + 1);
    }, []);

    const [updateCategory] = useMutation<assetLibraryQueriesUpdateAIAssetLibraryCategoryMutation>(updateAIAssetLibraryCategoryMutation);
    const [deleteCategory] = useMutation<assetLibraryQueriesDeleteAIAssetLibraryCategoryMutation>(deleteAIAssetLibraryCategoryMutation);

    const handleUpdateCategory = useCallback(
        (id: string, name: string, sortOrder: number) => {
            updateCategory({
                variables: {
                    id,
                    name: name.trim(),
                    sortOrder
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Category updated.', 'Admin') });
                    handleRefetch();
                },
                onError: (error) => {
                    console.error('Failed to update category:', error);
                    showErrorNotification({ message: T._('Failed updating category', 'Admin') });
                }
            });
        },
        [updateCategory, handleRefetch]
    );

    const handleDeleteCategory = useCallback(
        (id: string) => {
            deleteCategory({
                variables: {
                    id
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Category deleted.', 'Admin') });

                    if (selectedCategoryId === id) {
                        onCategorySelect(null);
                    }
                    handleRefetch();
                },
                onError: (error) => {
                    console.error('Failed to delete category:', error);
                    showErrorNotification({ message: T._('Failed deleting category', 'Admin') });
                }
            });
        },
        [selectedCategoryId, deleteCategory, onCategorySelect, handleRefetch]
    );

    const handleEditClick = useCallback(
        (e: React.MouseEvent, category: CategoryNode) => {
            e.stopPropagation();
            showDialog(
                'edit-category',
                <InputDialog
                    label={T._('Category name', 'Admin')}
                    defaultValue={category.name}
                    required
                    onConfirm={(name) => {
                        closeDialog('edit-category');
                        handleUpdateCategory(category.id, name, category.sortOrder);
                    }}
                />
            );
        },
        [showDialog, closeDialog, handleUpdateCategory]
    );

    const handleDeleteClick = useCallback(
        (e: React.MouseEvent, category: CategoryNode) => {
            e.stopPropagation();
            showDialog(
                'delete-category',
                <QuestionDialog
                    destructive
                    message={stringFormat(
                        T._('Do you want to remove the category "{0}"? Assets in this category will become uncategorized.', 'Admin'),
                        category.name
                    )}
                    onYesClick={() => {
                        closeDialog('delete-category');
                        handleDeleteCategory(category.id);
                    }}
                />
            );
        },
        [showDialog, closeDialog, handleDeleteCategory]
    );

    return (
        <TreeContent>
            <CategoryItem $selected={selectedCategoryId === null} onClick={() => onCategorySelect(null)}>
                <CategoryName $selected={selectedCategoryId === null}>{T._('All Assets', 'Admin')}</CategoryName>
            </CategoryItem>
            {categories.map((category) => (
                <CategoryItem key={category.id} $selected={selectedCategoryId === category.id} onClick={() => onCategorySelect(category.id)}>
                    <CategoryName $selected={selectedCategoryId === category.id}>{category.name}</CategoryName>
                    <CategoryActions>
                        <ActionIcon $hoverable tooltip={T._('Edit', 'Admin')} onClick={(e) => handleEditClick(e, category)}>
                            <PencilIcon />
                        </ActionIcon>
                        <ActionIcon $hoverable tooltip={T._('Delete', 'Admin')} onClick={(e) => handleDeleteClick(e, category)}>
                            <TrashIcon />
                        </ActionIcon>
                    </CategoryActions>
                </CategoryItem>
            ))}
        </TreeContent>
    );
};
